/**
 * EventQuotePage - Quote request page for Events & Functions
 * For services like Catering, Decoration that require quotes
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Star, MapPin, Check, Loader2, Utensils, Palette, Image } from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'sonner';
import CateringQuoteFlow from '../../components/events/CateringQuoteFlow';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventQuotePage() {
  const { providerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quoteOpen, setQuoteOpen] = useState(false);
  
  const serviceType = searchParams.get('type') || provider?.service_type;
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  useEffect(() => {
    const fetchProvider = async () => {
      if (!providerId) return;
      try {
        const response = await fetch(`${API_URL}/api/events/providers/${providerId}`);
        if (response.ok) {
          const data = await response.json();
          setProvider(data);
          // Auto-open quote modal
          setQuoteOpen(true);
        } else {
          toast.error('Provider not found');
          navigate('/events');
        }
      } catch (error) {
        console.error('Failed to fetch provider:', error);
        toast.error('Failed to load provider details');
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [providerId, navigate]);

  const handleQuoteSubmitted = () => {
    setQuoteOpen(false);
    toast.success('Quote request sent! Provider will respond within 24 hours.');
    navigate('/events');
  };

  const getProviderIcon = () => {
    switch (provider?.service_type) {
      case 'catering-services': return Utensils;
      case 'event-decoration': return Palette;
      case 'photography-videography': return Image;
      default: return Utensils;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <p className="text-gray-600 mb-4">Provider not found</p>
        <Button onClick={() => navigate('/events')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>
      </div>
    );
  }

  const Icon = getProviderIcon();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/events')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Events
          </button>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Icon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{provider.provider_name}</h1>
              <p className="text-orange-200 capitalize">{provider.service_type?.replace(/-/g, ' ')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Provider Info */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>About {provider.provider_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">{provider.description}</p>
              
              <div className="flex items-center gap-4 mb-4">
                <span className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <span className="font-bold">{provider.rating?.toFixed(1) || 'New'}</span>
                  {provider.review_count > 0 && (
                    <span className="text-gray-500">({provider.review_count} reviews)</span>
                  )}
                </span>
                <span className="flex items-center gap-1 text-gray-500">
                  <MapPin className="w-4 h-4" />
                  {provider.city}
                </span>
              </div>

              {/* Event Types */}
              {provider.event_types?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">Specializes in</h4>
                  <div className="flex flex-wrap gap-2">
                    {provider.event_types.map(type => (
                      <Badge key={type} variant="outline" className="capitalize">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Catering-specific info */}
              {provider.catering_details && (
                <div className="space-y-3">
                  {provider.catering_details.min_guests && (
                    <p className="text-sm text-gray-600">
                      <strong>Minimum guests:</strong> {provider.catering_details.min_guests}
                    </p>
                  )}
                  {provider.catering_details.dietary_options && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Dietary Options:</p>
                      <div className="flex flex-wrap gap-1">
                        {provider.catering_details.dietary_options.map(opt => (
                          <Badge key={opt} variant="outline" className="text-xs capitalize">
                            {opt.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Verification */}
              {provider.verification_level >= 2 && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 mt-4">
                  <p className="text-green-800 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Verified Provider
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quote Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Get Quote</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <span className="text-gray-500 text-sm">Starting from</span>
                <p className="text-2xl font-bold text-orange-600">
                  {currencySymbol}{provider.starting_price?.toLocaleString()}
                </p>
                {provider.price_per_head && (
                  <p className="text-sm text-gray-500">per head (varies by menu)</p>
                )}
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-orange-500 to-red-500"
                onClick={() => setQuoteOpen(true)}
                data-testid="open-quote-btn"
              >
                Request Quote
              </Button>
              
              <p className="text-xs text-gray-500 mt-3 text-center">
                Free quote - provider will respond within 24h
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Price Tiers (for Caterers) */}
        {provider.price_per_head?.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Menu Tiers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {provider.price_per_head.map((tier, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 border rounded-lg ${tier.is_popular ? 'border-orange-400 bg-orange-50' : ''}`}
                  >
                    {tier.is_popular && (
                      <Badge className="mb-2 bg-orange-500">Most Popular</Badge>
                    )}
                    <h4 className="font-bold text-lg">{tier.name}</h4>
                    <p className="text-2xl font-bold text-orange-600 mt-2">
                      {currencySymbol}{tier.price}<span className="text-sm font-normal">/head</span>
                    </p>
                    {tier.description && (
                      <p className="text-sm text-gray-600 mt-2">{tier.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quote Modals */}
      {provider.service_type === 'catering-services' && (
        <CateringQuoteFlow
          open={quoteOpen}
          onClose={() => setQuoteOpen(false)}
          provider={provider}
          onQuoteSubmitted={handleQuoteSubmitted}
        />
      )}
    </div>
  );
}
